import axios, { CancelTokenSource } from "axios";
import { AnalysisResult, AnalyzeRedlineResult, RedlinePiece } from "@/types/risk-analysis";
import { DocumentParagraph } from "@/types/document";
import { Performance } from "@/utils/performance";
import api from "./api";
import log from "../utils/logger";

export const runRiskAnalysis = async (
  playbookRuleId: string,
  fallbackRuleId: string,
  promptConfigId: string,
  userId: string,
  paragraphs: Array<DocumentParagraph>
): Promise<{ result: AnalysisResult; errors: (keyof AnalysisResult)[] }> => {
  const performanceProcessId = Performance.startProcess("runRiskAnalysis");
  try {
    const response = await api.post(
      "/risk-analysis",
      {
        playbookRuleId,
        fallbackRuleId,
        promptConfigId,
        userId,
        // no dirty text should be sent to the LLM
        paragraphs: paragraphs.map((paragraph) => ({ id: paragraph.id, text: paragraph.text })),
      },
      {
        cancelToken: riskAnalysisCancelTokens.riskAnalysisAPI.token,
        timeout: 300000, // 5 minutes - handles large documents
      }
    );
    return response.data.body;
  } catch (error) {
    log.error("Analysis error", undefined, error);
    throw error;
  } finally {
    Performance.endProcess(performanceProcessId, {
      playbookRuleId,
      promptConfigId,
      userId,
      paragraphs: paragraphs.length,
    });
  }
};

/**
 * Runs the analyze redline API call for a specific redline piece.
 * Adds a cancel token for the request to the analyzeRedlineCancelTokens array,
 * and removes it after completion or error.
 *
 * @param playbookRuleId - The ID of the playbook rule.
 * @param promptConfigId - The ID of the prompt configuration.
 * @param userId - The ID of the user.
 * @param paragraphs - The paragraphs to analyze.
 * @param originalRedlinePiece - The original redline piece to analyze.
 * @returns The analysis result and any errors related to the original piece.
 */
export const runAnalyzeRedline = async (
  playbookRuleId: string,
  fallbackRuleId: string,
  promptConfigId: string,
  userId: string,
  paragraphs: Array<Pick<DocumentParagraph, "id" | "text">>,
  originalRedlinePiece: RedlinePiece
): Promise<{ result: AnalyzeRedlineResult; errors: "originalPiece"[] }> => {
  const cancelToken = axios.CancelToken.source();
  analyzeRedlineCancelTokens.push({ [originalRedlinePiece.id]: cancelToken });

  try {
    const response = await api.post(
      "/analyze-redline",
      {
        playbookRuleId,
        fallbackRuleId,
        promptConfigId,
        userId,
        // no dirty text should be sent to the LLM
        paragraphs: paragraphs.map((paragraph) => ({ id: paragraph.id, text: paragraph.text })),
        originalPiece: originalRedlinePiece,
      },
      {
        cancelToken: cancelToken.token,
        timeout: 300000, // 5 minutes - handles large documents
      }
    );
    return response.data.body;
  } catch (error) {
    log.error("Analyze redline error", undefined, error);
    throw error;
  } finally {
    const index = analyzeRedlineCancelTokens.findIndex((token) => token[originalRedlinePiece.id]);
    if (index !== -1) {
      analyzeRedlineCancelTokens.splice(index, 1);
    }
  }
};

export const analyzeRedlineCancelTokens: Array<{ [key: string]: CancelTokenSource }> = [];

export const riskAnalysisCancelTokens: { [key: string]: CancelTokenSource } = {
  label: axios.CancelToken.source(),
  fullAnalysis: axios.CancelToken.source(),
  valueExtraction: axios.CancelToken.source(),
  summary: axios.CancelToken.source(),
  stoplightScore: axios.CancelToken.source(),
  redlines: axios.CancelToken.source(),
  riskAnalysisAPI: axios.CancelToken.source(),
};

export const resetRiskAnalysisCanceledTokens = () => {
  Object.keys(riskAnalysisCancelTokens).forEach((key) => {
    riskAnalysisCancelTokens[key] = axios.CancelToken.source();
  });
};
